import { motion, useMotionValue, animate } from 'framer-motion'
import { useRef, useState, useLayoutEffect } from 'react'
import personFillIcon from '../assets/person-fill.png'
import stackFillIcon from '../assets/stack-fill.png'

/* ═══════════════════════════════════════════════════════════
   NAV BAR — EXPERIMENT 1: Lens refraction + rim + specular
   ═══════════════════════════════════════════════════════════ */
const CONFIG = {
  // ─── Glass surface ──────────────────────────────────
  bgTint: 'rgba(255, 255, 255, 0.05)',
  blur: 3,                  // sharp content, just bent
  saturation: 130,
  borderColor: 'rgba(255, 255, 255, 0.08)',

  // ─── LENS REFRACTION ─────────────────────────────────
  lensStrength: 70,         // px — how much content bends near edges
  lensFalloff: 18,          // % — pill "edge zone" size (smaller = bend kicks in earlier)
  lensSmoothing: 0.25,      // sub-pixel blur after displacement (kills grain)

  // ─── RIM HIGHLIGHT (top edge line) ───────────────────
  rimOpacity: 0.08,
  rimSize: 0.5,

  // ─── Active pill ────────────────────────────────────
  pillBg: 'rgba(255, 255, 255, 0.20)',
  pillBorder: 'rgba(255, 255, 255, 0.24)',
  pillInnerLight: 'rgba(255, 255, 255, 0.22)',

  // ─── Shadow ─────────────────────────────────────────
  shadow: '0 8px 28px rgba(0,0,0,0.22)',
  innerLight: 'inset 0 0.5px 0 rgba(255,255,255,0.18)',

  // ─── Layout ─────────────────────────────────────────
  bottomInset: 16,
  containerPadding: 6,
  buttonPaddingX: 24,
  buttonPaddingY: 10,
  buttonGap: 4,

  // ─── Drag feel ──────────────────────────────────────
  dragFriction: 0.75,
  maxEdgeStretch: 24,
  edgeElastic: 0.18,
  tapThreshold: 5,
  snapStiffness: 420,
  snapDamping: 32,
  snapMass: 0.9,

  // ─── Press-and-hold to drag (iOS 26 liquid-glass feel) ──
  // The pill only starts following the finger AFTER it's been held
  // still for `holdDelayMs`. A quick tap (release before the delay)
  // just switches tabs. Moving more than `holdMoveTolerance` px before
  // the hold engages cancels it (so a scroll/flick isn't hijacked).
  holdDelayMs: 160,
  holdMoveTolerance: 10,
  // When the hold engages, the pill "lifts" slightly — subtle scale pop.
  liftScale: 1.06,
}

/* ── Nav Icons ── */

const HomeIcon = ({ active }) => (
  <svg width="32" height="32" viewBox="0 0 256 256" fill={active ? 'var(--color-primary)' : 'currentColor'}>
    <path d="M224,115.53906v92.46a16.08415,16.08415,0,0,1-8.44379,14.14416,15.40625,15.40625,0,0,1-7.53406,1.85584H159.96484a8,8,0,0,1-8-8V167.9917a8,8,0,0,0-8-8h-32a8,8,0,0,0-8,8V215.999a8,8,0,0,1-8,8H48.002a15.91729,15.91729,0,0,1-11.99132-5.40349A16.47275,16.47275,0,0,1,32,207.63379l0-92.09522a16.03108,16.03108,0,0,1,5.2373-11.83886l79.99317-72.73389a15.93657,15.93657,0,0,1,21.52637-.00146l80.00683,72.73584A16.03567,16.03567,0,0,1,224,115.53906Z"/>
  </svg>
)

const LibraryIcon = ({ active }) => (
  <div style={{
    width: 32, height: 32,
    backgroundColor: active ? 'var(--color-primary)' : '#ffffff',
    WebkitMaskImage: `url(${stackFillIcon})`, maskImage: `url(${stackFillIcon})`,
    WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center', maskPosition: 'center',
    WebkitMaskSize: 'contain', maskSize: 'contain',
  }} />
)

const ProfileIcon = ({ active }) => (
  <div style={{
    width: 32, height: 32,
    backgroundColor: active ? 'var(--color-primary)' : '#ffffff',
    WebkitMaskImage: `url(${personFillIcon})`, maskImage: `url(${personFillIcon})`,
    WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center', maskPosition: 'center',
    WebkitMaskSize: 'contain', maskSize: 'contain',
  }} />
)

const tabs = [
  { id: 'home', Icon: HomeIcon },
  { id: 'library', Icon: LibraryIcon },
  { id: 'settings', Icon: ProfileIcon },
]

function NavBar({ activeTab, setActiveTab, homeScrollRef, libraryScrollRef, libraryFilter, scrollToBottom, onLibraryNavClick }) {
  const containerRef = useRef(null)
  const tabRefs = useRef({})
  const isDraggingRef = useRef(false)
  const layoutCache = useRef({ tabBounds: {}, bounds: { left: 0, right: 0 } })
  const dragStart = useRef({ finger: 0, pill: 0 })
  const currentAnimation = useRef(null)

  // Press-and-hold bookkeeping: the timer that arms the drag, plus the
  // pointer's start position and the active pointer id while we wait.
  const holdTimerRef = useRef(null)
  const pendingDownRef = useRef(null)   // { finger, pillX, pointerId, target }

  const x = useMotionValue(0)
  const [pillBounds, setPillBounds] = useState({ left: 0, width: 0 })
  const [hoverTab, setHoverTab] = useState(activeTab)
  const [lifted, setLifted] = useState(false)   // pill "lift" pop while dragging

  const measureLayout = () => {
    const container = containerRef.current
    if (!container) return
    const containerRect = container.getBoundingClientRect()
    const newBounds = {}
    tabs.forEach(({ id }) => {
      const btn = tabRefs.current[id]
      if (!btn) return
      const r = btn.getBoundingClientRect()
      newBounds[id] = {
        left: r.left - containerRect.left,
        width: r.width,
        center: r.left - containerRect.left + r.width / 2,
      }
    })
    layoutCache.current.tabBounds = newBounds
    const first = newBounds[tabs[0].id]
    const last = newBounds[tabs[tabs.length - 1].id]
    if (first && last) {
      layoutCache.current.bounds = { left: first.left, right: last.left }
    }
  }

  useLayoutEffect(() => {
    measureLayout()
    const bounds = layoutCache.current.tabBounds[activeTab]
    if (!bounds) return
    if (!isDraggingRef.current) {
      setPillBounds({ left: bounds.left, width: bounds.width })
      setHoverTab(activeTab)
      if (currentAnimation.current) currentAnimation.current.stop()
      currentAnimation.current = animate(x, bounds.left, {
        type: 'spring',
        stiffness: CONFIG.snapStiffness,
        damping: CONFIG.snapDamping,
        mass: CONFIG.snapMass,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  useLayoutEffect(() => {
    const onResize = () => measureLayout()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const switchToTab = (tab) => {
    setActiveTab(tab)
    if (tab === 'home') setTimeout(() => scrollToBottom(homeScrollRef), 60)
    if (tab === 'library') setTimeout(() => onLibraryNavClick?.(), 60)
  }

  const handleTabClick = (tab) => {
    if (activeTab === tab) {
      if (tab === 'home' && homeScrollRef.current) {
        homeScrollRef.current.scrollTo({ top: homeScrollRef.current.scrollHeight, behavior: 'smooth' })
      }
      if (tab === 'library') onLibraryNavClick?.()
    } else {
      switchToTab(tab)
    }
  }

  const findNearestTab = (pillCenter) => {
    let nearest = activeTab
    let minDist = Infinity
    for (const { id } of tabs) {
      const b = layoutCache.current.tabBounds[id]
      if (!b) continue
      const d = Math.abs(b.center - pillCenter)
      if (d < minDist) { minDist = d; nearest = id }
    }
    return nearest
  }

  // Cancel a pending hold (timer + bookkeeping) without engaging drag.
  const clearHold = () => {
    if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null }
    pendingDownRef.current = null
  }

  // Actually engage the drag once the hold delay passes.
  const engageDrag = () => {
    const pending = pendingDownRef.current
    if (!pending) return
    if (currentAnimation.current) {
      currentAnimation.current.stop()
      currentAnimation.current = null
    }
    try { pending.target.setPointerCapture(pending.pointerId) } catch {}
    isDraggingRef.current = true
    dragStart.current = { finger: pending.finger, pill: x.get() }
    setLifted(true)
    holdTimerRef.current = null
  }

  const handlePointerDown = (e) => {
    // Don't engage immediately — wait for a press-and-hold. Record where the
    // finger went down and arm a timer; only then does the pill follow.
    pendingDownRef.current = {
      finger: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      target: e.currentTarget,
    }
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current)
    holdTimerRef.current = setTimeout(engageDrag, CONFIG.holdDelayMs)
  }

  const handlePointerMove = (e) => {
    // While waiting for the hold to arm: if the finger moves too far, this is
    // a scroll/flick, not a deliberate drag — cancel the pending hold.
    if (!isDraggingRef.current) {
      const pending = pendingDownRef.current
      if (pending) {
        const dx = Math.abs(e.clientX - pending.finger)
        const dy = Math.abs(e.clientY - pending.startY)
        if (dx > CONFIG.holdMoveTolerance || dy > CONFIG.holdMoveTolerance) clearHold()
      }
      return
    }
    const fingerDelta = e.clientX - dragStart.current.finger
    const pillDelta = fingerDelta * CONFIG.dragFriction
    let newX = dragStart.current.pill + pillDelta
    const { left, right } = layoutCache.current.bounds
    const maxStretch = CONFIG.maxEdgeStretch
    if (newX < left) {
      const overshoot = left - newX
      const stretched = (overshoot * CONFIG.edgeElastic * maxStretch) / (overshoot * CONFIG.edgeElastic + maxStretch)
      newX = left - stretched
    } else if (newX > right) {
      const overshoot = newX - right
      const stretched = (overshoot * CONFIG.edgeElastic * maxStretch) / (overshoot * CONFIG.edgeElastic + maxStretch)
      newX = right + stretched
    }
    x.set(newX)
    const pillCenter = newX + pillBounds.width / 2
    const nearest = findNearestTab(pillCenter)
    if (nearest !== hoverTab) setHoverTab(nearest)
  }

  const handlePointerUp = (e) => {
    // Released before the hold armed → it was a TAP. Switch to whichever tab
    // the finger is over (or the one under the pill), no dragging.
    if (!isDraggingRef.current) {
      clearHold()
      // figure out which tab was tapped from the pointer position
      const container = containerRef.current
      let tappedTab = null
      if (container) {
        const cx = e.clientX - container.getBoundingClientRect().left
        let best = Infinity
        for (const { id } of tabs) {
          const b = layoutCache.current.tabBounds[id]
          if (!b) continue
          const d = Math.abs(b.center - cx)
          if (d < best) { best = d; tappedTab = id }
        }
      }
      if (tappedTab) handleTabClick(tappedTab)
      setHoverTab(tappedTab || activeTab)
      return
    }

    // Was dragging → snap to nearest tab.
    isDraggingRef.current = false
    setLifted(false)
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch {}
    const pillCenter = x.get() + pillBounds.width / 2
    const nearest = findNearestTab(pillCenter)
    const bounds = layoutCache.current.tabBounds[nearest]
    if (bounds) {
      currentAnimation.current = animate(x, bounds.left, {
        type: 'spring',
        stiffness: CONFIG.snapStiffness,
        damping: CONFIG.snapDamping,
        mass: CONFIG.snapMass,
      })
    }
    if (nearest !== activeTab) {
      switchToTab(nearest)
    } else {
      setHoverTab(nearest)
    }
  }

  const handlePointerCancel = () => {
    clearHold()
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    setLifted(false)
    setHoverTab(activeTab)
    const b = layoutCache.current.tabBounds[activeTab]
    if (b) {
      currentAnimation.current = animate(x, b.left, {
        type: 'spring',
        stiffness: CONFIG.snapStiffness,
        damping: CONFIG.snapDamping,
        mass: CONFIG.snapMass,
      })
    }
  }

  return (
    <>
      {/* LENS REFRACTION FILTER — sRGB color space + post-blur to kill grain */}
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }} aria-hidden="true">
        <defs>
          <radialGradient id="navbar-lens-map" cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="rgb(128,128,128)" />
            <stop offset={`${CONFIG.lensFalloff}%`} stopColor="rgb(128,128,128)" />
            <stop offset="85%" stopColor="rgb(255,40,40)" />
            <stop offset="100%" stopColor="rgb(40,255,40)" />
          </radialGradient>
          <filter id="navbar-lens-distortion" x="-10%" y="-10%" width="120%" height="120%" colorInterpolationFilters="sRGB">
            <feImage xlinkHref="#navbar-lens-rect" x="0" y="0" width="100%" height="100%" result="lensMap" preserveAspectRatio="none" />
            <feDisplacementMap in="SourceGraphic" in2="lensMap" scale={CONFIG.lensStrength} xChannelSelector="R" yChannelSelector="G" result="bent" />
            <feGaussianBlur in="bent" stdDeviation={CONFIG.lensSmoothing} />
          </filter>
          <rect id="navbar-lens-rect" width="100%" height="100%" fill="url(#navbar-lens-map)" />
        </defs>
      </svg>

      <div style={{
        position: 'fixed',
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${CONFIG.bottomInset}px)`,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
      }}>
        <div
          ref={containerRef}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: `${CONFIG.buttonGap}px`,
            padding: `${CONFIG.containerPadding}px`,
            borderRadius: '100px',
            background: CONFIG.bgTint,
            backdropFilter: `url(#navbar-lens-distortion) saturate(${CONFIG.saturation}%) blur(${CONFIG.blur}px)`,
            WebkitBackdropFilter: `saturate(${CONFIG.saturation}%) blur(${CONFIG.blur}px)`,
            border: `0.5px solid ${CONFIG.borderColor}`,
            boxShadow: `${CONFIG.shadow}, ${CONFIG.innerLight}`,
            position: 'relative',
            transform: 'translateZ(0)',
            willChange: 'backdrop-filter',
            isolation: 'isolate',
          }}
        >
         

          {/* CRISP TOP-EDGE LINE */}
          <div style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '100px',
            pointerEvents: 'none',
            boxShadow: `inset 0 1px 0 rgba(255,255,255,${CONFIG.rimOpacity})`,
            zIndex: 0,
          }} />

          {/* LAYER 1 — Visible pill (lifts slightly while dragging) */}
          {pillBounds.width > 0 && (
            <motion.div
              animate={{ scale: lifted ? CONFIG.liftScale : 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              style={{
                position: 'absolute',
                top: CONFIG.containerPadding,
                left: 0,
                x,
                width: pillBounds.width,
                height: `calc(100% - ${CONFIG.containerPadding * 2}px)`,
                borderRadius: '100px',
                background: CONFIG.pillBg,
                border: `0.5px solid ${CONFIG.pillBorder}`,
                boxShadow: `inset 0 0.5px 0 ${CONFIG.pillInnerLight}`,
                zIndex: 1,
                pointerEvents: 'none',
              }}
            />
          )}

          {/* LAYER 2 — Tab buttons */}
          {tabs.map(({ id, Icon }) => {
            const isHovered = hoverTab === id
            return (
              <motion.button
                key={id}
                ref={el => { tabRefs.current[id] = el }}
                onClick={() => handleTabClick(id)}
                whileTap={{ scale: 0.92 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                style={{
                  position: 'relative',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: `${CONFIG.buttonPaddingY}px ${CONFIG.buttonPaddingX}px`,
                  borderRadius: '100px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isHovered ? 'var(--color-primary)' : '#fff',
                  zIndex: 2,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <div style={{ position: 'relative' }}>
                  <Icon active={isHovered} />
                </div>
              </motion.button>
            )
          })}

          {/* LAYER 3 — Drag surface (press-and-hold to engage). The button
              taps underneath still work for quick taps; this only hijacks the
              gesture once the hold timer arms. */}
          {pillBounds.width > 0 && (
            <motion.div
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
              style={{
                position: 'absolute',
                top: CONFIG.containerPadding,
                left: 0,
                x,
                width: pillBounds.width,
                height: `calc(100% - ${CONFIG.containerPadding * 2}px)`,
                borderRadius: '100px',
                background: 'transparent',
                zIndex: 3,
                touchAction: 'pan-y',
                cursor: 'grab',
              }}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default NavBar